import React, { useState, useEffect } from 'react';
import projectsData from '../data/projects.json';
import '../styles/projects.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faGithub, 
  faYoutube 
} from '@fortawesome/free-brands-svg-icons';
import { 
  faExternalLinkAlt,
  faFileAlt,
  faTimes,
  faChevronLeft,
  faChevronRight,
  faCalendarAlt,
  faClock,
  faStar,
  faArrowRight
} from '@fortawesome/free-solid-svg-icons';

const Projects = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState({});
  const [isHovering, setIsHovering] = useState(false);

  // Auto-rotate project images
  useEffect(() => {
    if (isHovering || selectedProject) return;
    const intervalId = setInterval(() => {
      setActiveImageIndex((prevIndexes) => {
        const newIndexes = {};
        projectsData.forEach((project) => {
          const currentIndex = prevIndexes[project.id] || 0;
          newIndexes[project.id] = (currentIndex + 1) % project.medias.length;
        });
        return newIndexes;
      });
    }, 4500);

    return () => clearInterval(intervalId);
  }, [isHovering, selectedProject]);

  const handleCardClick = (project, imageIndex) => {
    setSelectedProject(project);
    setActiveImageIndex((prev) => ({
      ...prev,
      [project.id]: imageIndex
    }));
  };

  const handleCloseModal = () => {
    setSelectedProject(null);
  };

  const handlePrevImage = (projectId, e) => {
    e.stopPropagation();
    setActiveImageIndex((prev) => {
      const currentIndex = prev[projectId] || 0;
      const medias = projectsData.find(p => p.id === projectId).medias;
      return {
        ...prev,
        [projectId]: (currentIndex - 1 + medias.length) % medias.length
      };
    });
  };

  const handleNextImage = (projectId, e) => {
    e.stopPropagation();
    setActiveImageIndex((prev) => {
      const currentIndex = prev[projectId] || 0;
      const medias = projectsData.find(p => p.id === projectId).medias;
      return {
        ...prev,
        [projectId]: (currentIndex + 1) % medias.length
      };
    });
  };

  const renderIcon = (iconName) => {
    switch (iconName) {
      case 'github': return <FontAwesomeIcon icon={faGithub} />;
      case 'external-link': return <FontAwesomeIcon icon={faExternalLinkAlt} />;
      case 'file': return <FontAwesomeIcon icon={faFileAlt} />;
      case 'youtube': return <FontAwesomeIcon icon={faYoutube} />;
      default: return <FontAwesomeIcon icon={faExternalLinkAlt} />;
    }
  };

  const isVideoUrl = (url) => {
    return url.includes("youtube.com") || url.includes("youtu.be") || url.includes("loom.com");
  };

  const getVideoEmbedUrl = (videoUrl) => {
    if (!videoUrl) return '';
    if (videoUrl.includes("youtube.com") || videoUrl.includes("youtu.be")) {
      const videoId = videoUrl.includes("youtu.be") 
        ? videoUrl.split("/")[3]?.split("?")[0]
        : videoUrl.split("v=")[1]?.split("&")[0];
      return `https://www.youtube.com/embed/${videoId}?autoplay=0&rel=0`;
    } else if (videoUrl.includes("vimeo.com")) {
      const videoId = videoUrl.split("/")[3];
      return `https://player.vimeo.com/video/${videoId}`;
    } else if (videoUrl.includes("loom.com")) {
      const videoId = videoUrl.split("/")[4]?.split("?")[0];
      return `https://www.loom.com/embed/${videoId}`;
    }
    return videoUrl;
  };

  const renderMedia = (media, project, index, isModal = false) => {
    if (isVideoUrl(media)) {
      return (
        <iframe
          src={getVideoEmbedUrl(media)}
          title={`${project.title} video ${index + 1}`}
          className={isModal ? "modal-image media-video" : "project-image media-video"}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        ></iframe>
      );
    }
    return (
      <img
        src={media}
        alt={`${project.title} screenshot ${index + 1}`}
        className={isModal ? "modal-image" : "project-image"}
        onClick={!isModal ? () => handleCardClick(project, index) : undefined}
      />
    );
  };

  return (
    <div className="projects-container">
      <div className="projects-heading">
        <FontAwesomeIcon icon={faStar} className="heading-icon" />
        <h1>Featured Works</h1>
        <p>A collection of high-impact technical projects across Fintech, ERP, and E-commerce ecosystems.</p>
      </div>

      <div className="projects-grid">
        {projectsData.map((project) => (
          <div 
            key={project.id} 
            className="project-card"
            onClick={() => handleCardClick(project, activeImageIndex[project.id] || 0)}
          >
            <div 
              className="project-media"
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => setIsHovering(false)}
            >
              {project.category && <span className="category-tag">{project.category}</span>}
              {renderMedia(project.medias[activeImageIndex[project.id] || 0], project, activeImageIndex[project.id] || 0)}
              <div className="image-counter">
                {(activeImageIndex[project.id] || 0) + 1}/{project.medias.length}
              </div>
              <button className="carousel-button prev" onClick={(e) => handlePrevImage(project.id, e)}>
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              <button className="carousel-button next" onClick={(e) => handleNextImage(project.id, e)}>
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>

            <div className="project-content">
              <h3 className="project-title">{project.title}</h3>
              <p className="project-description">{project.description}</p>
              
              <div className="project-tech">
                {project.technologies.slice(0, 4).map((tech, index) => (
                  <span key={index} className="tech-tag">{tech}</span>
                ))}
                {project.technologies.length > 4 && (
                  <span className="tech-tag">+{project.technologies.length - 4}</span>
                )}
              </div>

              {(project.duration || project.year) && (
                <div className="project-stats">
                  {project.duration && (
                    <div className="stat-item">
                      <span className="stat-label">Duration</span>
                      <span className="stat-value">{project.duration}</span>
                    </div>
                  )}
                  {project.year && (
                    <div className="stat-item">
                      <span className="stat-label">Delivery</span>
                      <span className="stat-value">{project.year}</span>
                    </div>
                  )}
                </div>
              )}
              
              <div className="project-actions">
                <button 
                  className="button button-primary"
                  onClick={(e) => { e.stopPropagation(); handleCardClick(project, activeImageIndex[project.id] || 0); }}
                >
                  Deep Dive <FontAwesomeIcon icon={faArrowRight} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {selectedProject && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{selectedProject.title}</h2>
              <button className="modal-close" onClick={handleCloseModal}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className="modal-body">
              <div 
                className="project-media modal-media"
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
              >
                {renderMedia(selectedProject.medias[activeImageIndex[selectedProject.id] || 0], selectedProject, activeImageIndex[selectedProject.id] || 0, true)}
                <div className="image-counter">
                  {(activeImageIndex[selectedProject.id] || 0) + 1}/{selectedProject.medias.length}
                </div>
                <button className="carousel-button prev" onClick={(e) => handlePrevImage(selectedProject.id, e)}>
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <button className="carousel-button next" onClick={(e) => handleNextImage(selectedProject.id, e)}>
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>

              <div className="details-grid">
                <div>
                  <div className="detail-section">
                    <h3 className="detail-title">Project Overview</h3>
                    <p className="detail-content">{selectedProject.details || selectedProject.description}</p>
                  </div>
                  
                  <div className="detail-section">
                    <h3 className="detail-title">Key Contributions</h3>
                    <ul className="responsibilities-list">
                      {selectedProject.responsibilities.map((resp, index) => (
                        <li key={index} className="responsibility-item">{resp}</li>
                      ))}
                    </ul>
                  </div>
                </div>
                
                <div className="side-panels">
                  <div className="info-card">
                    <div className="detail-section">
                      <h3 className="detail-title">Technologies</h3>
                      <div className="project-tech">
                        {selectedProject.technologies.map((tech, index) => (
                          <span key={index} className="tech-tag">{tech}</span>
                        ))}
                      </div>
                    </div>
                    
                    <div className="detail-section">
                      <h3 className="detail-title">Business Impact</h3>
                      <p className="detail-content">{selectedProject.impact}</p>
                    </div>

                    <div className="detail-section">
                      <h3 className="detail-title">Timeline</h3>
                      <div className="project-stats" style={{border: 'none', paddingTop: 0}}>
                        <div className="stat-item">
                          <FontAwesomeIcon icon={faClock} style={{color: 'var(--primary)', marginBottom: '0.5rem'}} />
                          <span className="stat-label">Duration</span>
                          <span className="stat-value">{selectedProject.duration || 'N/A'}</span>
                        </div>
                        <div className="stat-item">
                          <FontAwesomeIcon icon={faCalendarAlt} style={{color: 'var(--primary)', marginBottom: '0.5rem'}} />
                          <span className="stat-label">Year</span>
                          <span className="stat-value">{selectedProject.year || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="modal-actions" style={{marginTop: '2rem'}}>
                    {selectedProject.links.map((link, index) => (
                      <a
                        key={index}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="button button-primary"
                        style={{width: '100%', marginBottom: '0.75rem'}}
                      >
                        {renderIcon(link.icon)} {link.title}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Projects;